import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, CalendarDays, Users, Hash } from "lucide-react";

export interface GuardLead {
  title?: string | null;
  client_name?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  group_reference?: string | null;
  pax_count?: number | null;
  status?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  lead: GuardLead | null | undefined;
  /** Verb shown on the confirm button, e.g. "Copy link" or "Continue to email". */
  actionLabel: string;
  onConfirm: () => void;
}

function fmt(d?: string | null) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

/** Returns true when sending an intake link from this programme is suspicious. */
export function isRiskyIntakeLead(lead?: GuardLead | null): boolean {
  if (!lead) return false;
  const status = (lead.status || "").toLowerCase();
  if (["cancelled", "completed", "aborted"].includes(status)) return true;
  if (lead.end_date) {
    const end = new Date(lead.end_date);
    const todayZa = new Date(Date.now() + 2 * 60 * 60 * 1000);
    todayZa.setUTCHours(0, 0, 0, 0);
    if (end < todayZa) return true;
  }
  return false;
}

export function IntakeLinkGuardDialog({ open, onOpenChange, lead, actionLabel, onConfirm }: Props) {
  const [confirmed, setConfirmed] = useState(false);
  const [riskConfirmed, setRiskConfirmed] = useState(false);
  const risky = isRiskyIntakeLead(lead);

  useEffect(() => {
    if (open) {
      setConfirmed(false);
      setRiskConfirmed(false);
    }
  }, [open]);

  const dates = [fmt(lead?.start_date), fmt(lead?.end_date)].filter(Boolean).join(" – ");
  const canProceed = confirmed && (!risky || riskConfirmed);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Check the programme before you send</AlertDialogTitle>
          <AlertDialogDescription>
            This intake link belongs to the programme below. Anyone who opens it will be saved onto this programme.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="rounded-md border p-3 space-y-2 text-sm">
          <p className="font-semibold">{lead?.title || "Untitled programme"}</p>
          {lead?.client_name && <p className="text-muted-foreground">{lead.client_name}</p>}
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-1">
            {dates && (
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" /> {dates}
              </span>
            )}
            {lead?.pax_count ? (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" /> {lead.pax_count} expected
              </span>
            ) : null}
            {lead?.group_reference && (
              <span className="flex items-center gap-1">
                <Hash className="h-3 w-3" /> {lead.group_reference}
              </span>
            )}
            {lead?.status && <span className="capitalize">Status: {lead.status}</span>}
          </div>
        </div>

        {risky && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-3 space-y-2">
            <p className="text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              This programme has already ended or is no longer active. Sending an intake link from here is almost
              certainly the wrong programme.
            </p>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={riskConfirmed} onCheckedChange={(v) => setRiskConfirmed(v === true)} className="mt-0.5" />
              <span>I know this programme has ended and still want to continue.</span>
            </label>
          </div>
        )}

        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(v === true)} className="mt-0.5" />
          <span>I confirm this is the correct programme for the people I am sending this to.</span>
        </label>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!canProceed}
            onClick={(e) => {
              e.preventDefault();
              if (!canProceed) return;
              onOpenChange(false);
              onConfirm();
            }}
          >
            {actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
